import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Meter } from '@/components/ui/stat-tile';
import { useIsCompact } from '@/hooks/use-media-query';
import { SortableTable, type SortableColumn } from '@/components/ui/sortable-table';

const TOP_COUNT = 5;

/**
 * The default view for a breakdown: the top rows as share-of-total meters —
 * "participation", the way the old Trends charts showed it — with the full
 * sortable, paginated table one click away in a slide-over. Collapsing
 * straight to the table lost that at-a-glance read; this restores it without
 * bringing the table itself back to the top of the page.
 */
export function DashboardQuickView<T>({
  title,
  rows,
  loading,
  busy,
  detail,
  getId,
  getName,
  getValue,
  getShare,
  isMoney = true,
  nameLabel,
  valueLabel,
  columns,
  search,
}: {
  title: string;
  rows: T[];
  /** Nothing to show yet: the list is replaced by skeleton rows. */
  loading: boolean;
  /** Rows are on screen and being refreshed: they stay, dimmed. */
  busy?: boolean;
  getId: (row: T) => string;
  getName: (row: T) => string;
  getValue: (row: T) => number;
  getShare: (row: T) => number;
  isMoney?: boolean;
  /** Column headings — what the name and the figure are. */
  nameLabel: string;
  valueLabel: string;
  columns: SortableColumn<T>[];
  /** Adds a search box to the detail sheet, filtering rows client-side. */
  search?: {
    placeholder: string;
    match: (row: T, query: string) => boolean;
  };
  /**
   * Replaces the sheet's default table for a breakdown whose detail is not a
   * slice of `rows` — one that pages or searches against the API instead.
   */
  detail?: ReactNode;
}) {
  const { t } = useTranslation();
  const isCompact = useIsCompact();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const top = useMemo(
    () =>
      [...rows].sort((a, b) => getValue(b) - getValue(a)).slice(0, TOP_COUNT),
    [rows, getValue]
  );

  const detailRows = useMemo(() => {
    if (!search || !query.trim()) return rows;
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => search.match(row, needle));
  }, [rows, query, search]);

  if (loading) {
    return (
      <div className="divide-y rounded-xl border">
        {Array.from({ length: TOP_COUNT }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-3">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-1.5 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="relative rounded-xl">
        <BusyOverlay show={busy} />
        <p
          className={cn(
            'text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm',
            busy && 'opacity-60 transition-opacity'
          )}
        >
          {t('dashboard.noData')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <BusyOverlay show={busy} />
        <div
          className={cn(
            'divide-y rounded-xl border',
            busy && 'opacity-60 transition-opacity'
          )}
        >
            {/* Says what the figure is. The rows stack on a phone, where a
              column heading would sit above nothing in particular, so there
              the section's help line carries it instead. */}
            <div className="text-muted-foreground hidden gap-4 px-3 py-2 text-xs sm:flex">
              <span className="flex-1">{nameLabel}</span>
              <span className="w-48">
                {t('dashboard.shareOf', { value: valueLabel.toLowerCase() })}
              </span>
              <span className="w-28 text-right">{valueLabel}</span>
            </div>
            {top.map((row) => (
              <div
                key={getId(row)}
                className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-4"
              >
                <span className="min-w-0 flex-1 truncate text-sm">
                  {getName(row)}
                </span>
                <div className="sm:w-48">
                  <Meter
                    label={t('dashboard.participation')}
                    value={`${getShare(row).toFixed(getShare(row) < 10 ? 1 : 0)}%`}
                    percent={getShare(row)}
                  />
                </div>
                <span className="text-right font-semibold tabular-nums sm:w-28">
                  {isMoney
                    ? formatCurrency(getValue(row))
                    : formatNumber(getValue(row))}
                </span>
              </div>
            ))}
        </div>
      </div>

      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        {t('dashboard.viewDetails')}
        <ArrowRight className="size-4" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side={isCompact ? 'bottom' : 'right'}
          className={cn(
            'gap-0 overflow-y-auto p-0',
            isCompact
              ? 'max-h-[85svh] rounded-t-xl'
              : 'w-full sm:max-w-3xl'
          )}
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          <div className="space-y-3 p-4">
            {detail ?? (
              <>
                {search && (
                  <Input
                    placeholder={search.placeholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                )}
                <SortableTable
                  rows={detailRows}
                  columns={columns}
                  rowKey={getId}
                  busy={busy}
                />
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
